import { memo, useState, type ReactNode } from "react";
import { TableCell, TableRow } from "@/components/ui/table";
import { Application } from "@/types/application";
import { formatCurrency, formatPtpDate } from "@/utils/formatters";
import {
  BadgeCheck,
  Building2,
  CalendarRange,
  Car,
  Check,
  ChevronDown,
  ChevronRight,
  IndianRupee,
  Layers,
  MessageSquareText,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import StatusBadge from "./StatusBadge";
import ApplicationDetails from "./ApplicationDetails";
import CallStatusDisplay from "../CallStatusDisplay";
import CommentsDisplay from "./CommentsDisplay";
import VehicleStatusBadge from "../VehicleStatusBadge";
import LoanTypeBadge from "../LoanTypeBadge";
import type { BatchComment } from "@/hooks/useBatchComments";
import type { BatchContactStatus } from "@/hooks/useBatchContactCallingStatus";

const COLUMN_COUNT = 6;

const StripeField = ({
  icon: Icon,
  label,
  children,
}: {
  icon: LucideIcon;
  label: string;
  children: ReactNode;
}) => (
  <div className="flex min-w-0 items-start gap-2.5">
    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-slate-500 shadow-sm">
      <Icon className="h-4 w-4" />
    </span>
    <div className="min-w-0">
      <p className="text-[11px] font-medium leading-4 text-gray-500">{label}</p>
      <div className="mt-0.5 break-words text-sm font-medium leading-5 text-gray-800">{children}</div>
    </div>
  </div>
);

interface ApplicationRowProps {
  application: Application;
  selectedApplicationId?: string;
  onRowClick: (application: Application) => void;
  selectedEmiMonth?: string | null;
  // Batched data props
  batchedStatus?: string;
  batchedPtpDate?: string | null;
  batchedContactStatus?: BatchContactStatus;
  batchedComments?: BatchComment[];
  isLoading?: boolean;
}

const ApplicationRow = memo(({ 
  application, 
  selectedApplicationId, 
  onRowClick,
  batchedPtpDate,
}: ApplicationRowProps) => {
  const comments = application.recent_comments || [];
  const [expanded, setExpanded] = useState(false);
  const isSelected = selectedApplicationId === application.id;

  const handleRowClick = (e: React.MouseEvent) => {
    // Don't navigate if user is selecting text (copying)
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      return;
    }
    onRowClick(application);
  };

  const toggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded((open) => !open);
  };

  const emiAmount =
    application.emi_amount != null && !isNaN(application.emi_amount)
      ? formatCurrency(application.emi_amount)
      : 'N/A';
  const showNach = application.nach_status !== undefined && application.nach_status !== null;
  const nachActive = Number(application.nach_status) === 1;
  const showNachReason = showNach && !nachActive && !!application.reason;

  const rowTone = isSelected
    ? 'bg-slate-100 border-l-2 border-l-slate-300'
    : 'border-l-2 border-l-transparent hover:bg-slate-100';

  return (
    <>
    <TableRow
      className={`group cursor-pointer transition-colors align-top ${rowTone} ${expanded ? 'border-b-0' : ''}`}
      onClick={handleRowClick}
    >
      {/* Application Details */}
      <TableCell className="min-w-[22rem] py-2.5 align-top">
        <div className="flex items-start gap-1.5">
          <Tooltip delayDuration={150}>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-expanded={expanded}
                aria-label={expanded ? 'Hide loan details' : 'Show loan details'}
                onClick={toggleExpanded}
                className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors hover:border-slate-400 hover:bg-slate-200 hover:text-slate-800"
              >
                {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="px-2 py-1 text-xs">
              {expanded ? 'Hide loan details' : 'Show loan details'}
            </TooltipContent>
          </Tooltip>
          <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-bold text-blue-800 underline-offset-2 group-hover:underline">{application.applicant_name}</span>
            <VehicleStatusBadge vehicleStatus={application.vehicle_status} />
            <LoanTypeBadge loanType={application.loan_type} />
            {(() => {
              const tags = application.special_case_tags || [];
              const hasProblematic = tags.some(tag => {
                if (!tag) return false;
                const normalizedTag = String(tag).toLowerCase().trim();
                return normalizedTag === 'problematic';
              });

              const hasThirdParty = tags.some(tag => {
                if (!tag) return false;
                const normalizedTag = String(tag).toLowerCase().trim();
                return normalizedTag === 'third party' || normalizedTag === 'thirdparty' || normalizedTag === '3rd party' || normalizedTag === '3rd-party';
              });

              return (
                <>
                  {hasProblematic && (
                    <span
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-700 text-white border border-red-800"
                      title="Problematic Case"
                    >
                      P
                    </span>
                  )}
                  {hasThirdParty && (
                    <span
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                      title="3rd Party Case"
                    >
                      3rd party
                    </span>
                  )}
                </>
              );
            })()}
          </div>
          <div className="flex flex-col gap-0.5 text-sm leading-5 text-gray-500">
            <p className="whitespace-nowrap">
              ID: <span className="ml-1 font-medium text-gray-600">{application.applicant_id}</span>
            </p>
            <p className="whitespace-nowrap">
              EMI: <span className="ml-1 font-medium text-gray-600">{emiAmount}</span>
            </p>
            <p className="flex flex-wrap items-center">
              <span className="inline-flex items-center whitespace-nowrap after:mx-3 after:text-gray-300 after:content-['|']">
                RM: <span className="ml-1 font-medium text-gray-600">{application.rm_name || 'N/A'}</span>
              </span>
              <span className="whitespace-nowrap">
                Branch: <span className="ml-1 font-medium text-gray-600">{application.branch_name}</span>
              </span>
            </p>
          </div>
          </div>
        </div>
      </TableCell>

      {/* Overdue */}
      <TableCell className="min-w-[15rem] py-2.5 align-top text-center">
        <div className="flex flex-col gap-1 items-center">
          <div className="text-sm grid grid-cols-[auto_auto_1fr] items-center gap-x-1">
            <span className="whitespace-nowrap text-gray-600">LMS as of 1st:</span>
            <span className="font-semibold text-blue-600 inline-flex items-center gap-1">
              {application.lms_overdue_as_of_1st != null && !isNaN(application.lms_overdue_as_of_1st)
                ? (
                  <>
                    <IndianRupee className="h-4 w-4" />
                    {application.lms_overdue_as_of_1st.toLocaleString('en-IN')}
                  </>
                )
                : 'N/A'}
            </span>
          </div>
          <div className="text-sm grid grid-cols-[auto_auto_1fr] items-center gap-x-1">
            <span className="whitespace-nowrap text-gray-600">LMS as of Today:</span>
            <span className="font-semibold text-blue-600 inline-flex items-center gap-1">
              {application.lms_overdue_as_of_today != null && !isNaN(application.lms_overdue_as_of_today)
                ? (
                  <>
                    <IndianRupee className="h-4 w-4" />
                    {application.lms_overdue_as_of_today.toLocaleString('en-IN')}
                  </>
                )
                : 'N/A'}
            </span>
          </div>
          <div className="text-sm grid grid-cols-[auto_auto_1fr] items-center gap-x-1">
            <span className="whitespace-nowrap text-gray-600">App as of Today:</span>
            <span className="font-semibold text-green-600 inline-flex items-center gap-1">
              {application.app_overdue_as_of_today != null && !isNaN(application.app_overdue_as_of_today)
                ? (
                  <>
                    <IndianRupee className="h-4 w-4" />
                    {application.app_overdue_as_of_today.toLocaleString('en-IN')}
                  </>
                )
                : 'N/A'}
            </span>
          </div>
        </div>
      </TableCell>

      {/* Status */}
      <TableCell className="min-w-[6.5rem] py-2.5 align-top text-center">
        <StatusBadge status={application.status} />
      </TableCell>

      {/* Date */}
      <TableCell className="min-w-[7.5rem] py-2.5 align-top text-center">
        <div className="flex flex-col gap-2 text-sm items-center">
          <div className="flex flex-col gap-0.5 w-full rounded-md border border-yellow-200 bg-yellow-50 px-2 py-1">
            <span className="text-xs font-medium text-gray-600">PTP</span>
            <span className="font-semibold text-gray-900 whitespace-nowrap">
              {batchedPtpDate
                ? formatPtpDate(batchedPtpDate)
                : application.ptp_date
                  ? formatPtpDate(application.ptp_date)
                  : "Not Set"}
            </span>
          </div>
        </div>
      </TableCell>


      {/* Amount Collected */}
      <TableCell className="min-w-[8.5rem] py-2.5 align-top text-center">
        <div className="text-black font-semibold text-base">
          {formatCurrency(application.amount_collected || 0)}
        </div>
      </TableCell>

      {/* Recent Comments */}
      <TableCell rowSpan={expanded ? 2 : undefined} className="w-56 max-w-[14rem] min-w-0 py-2.5 align-top overflow-hidden">
        <CommentsDisplay 
          comments={comments}
          hasComments={comments.length > 0}
          expanded={expanded}
        />
      </TableCell>
    </TableRow>
    {expanded && (
      <TableRow className={rowTone} onClick={handleRowClick}>
        <TableCell colSpan={COLUMN_COUNT - 1} className="px-4 pb-3 pt-0 align-top">
          <div className="grid grid-cols-4 gap-x-6 gap-y-4 rounded-xl bg-[#E7F1FB] px-5 py-3.5">
            <StripeField icon={Building2} label="Lender">
              {application.lender_name || application.lender || 'N/A'}
            </StripeField>
            <StripeField icon={Users} label="TL">{application.team_lead || 'N/A'}</StripeField>
            <StripeField icon={Car} label="RC Number">{application.rc_number || 'N/A'}</StripeField>
            <StripeField icon={CalendarRange} label="Tenure">
              {application.tenure != null ? `${application.tenure} months` : 'N/A'}
            </StripeField>
            <StripeField icon={Layers} label="DPD Bucket">
              {application.current_dpd_bucket ?? 'X'}
            </StripeField>
            {showNach && (
              <StripeField icon={BadgeCheck} label="NACH Status">
                <span className="inline-flex items-center">
                  {nachActive ? (
                    <Check className="h-3.5 w-3.5 text-green-600" />
                  ) : (
                    <X className="h-3.5 w-3.5 text-red-600" />
                  )}
                </span>
              </StripeField>
            )}
            {showNachReason && (
              <StripeField icon={MessageSquareText} label="NACH Reason">{application.reason}</StripeField>
            )}
          </div>
        </TableCell>
      </TableRow>
    )}
    </>
  );
});

ApplicationRow.displayName = "ApplicationRow";

export default ApplicationRow;
